import React, { useEffect, useState } from "react";
import API from "../axiosInstance";
import "../styles/GiftCatalogue.css";

/*
  GiftStore
  - Affiche le solde réel depuis la BDD
  - Affiche le catalogue cadeaux (CSS pur, sans Bootstrap)
  - Envoie un cadeau
  - Déclenche animation petit / moyen / gros
  - Bloque si pas assez de coins
*/

function GiftStore({
  receiverEmail,
  context = "video",
  videoId = null,
  onNotEnoughCoins,
  onSent,
}) {
  // ===============================
  // UTILISATEUR CONNECTÉ
  // ===============================
  const currentUser = JSON.parse(
    localStorage.getItem("currentUser") || "null"
  );

  const currentUserEmail = currentUser?.email || null;

  // ===============================
  // STATES
  // ===============================
  const [gifts, setGifts] = useState([]);
  const [coinsBalance, setCoinsBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState("");

  // ===============================
  // LOAD SOLDE (BACKEND)
  // ===============================
  useEffect(() => {
    if (!currentUserEmail) return;

    API.get(`/coins/balance/${encodeURIComponent(currentUserEmail)}`)
      .then((res) => {
        setCoinsBalance(Number(res.data?.coins_balance || 0));
      })
      .catch((err) => {
        console.error("❌ Erreur chargement solde :", err);
        setCoinsBalance(0);
      });
  }, [currentUserEmail]);

  // ===============================
  // LOAD CATALOGUE CADEAUX
  // ===============================
  useEffect(() => {
    API.get("/gifts/catalog")
      .then((res) => {
        setGifts(Array.isArray(res.data) ? res.data : []);
      })
      .catch((err) => {
        console.error("❌ Erreur chargement catalogue :", err);
        setMessage("Erreur lors du chargement des cadeaux.");
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  // ===============================
  // ENVOI CADEAU
  // ===============================
  const handleSendGift = async (gift) => {
    if (!currentUserEmail || !receiverEmail) {
      setMessage("Utilisateur ou destinataire manquant.");
      return;
    }

    // ❌ PAS ASSEZ DE COINS
    if (coinsBalance < gift.coin_cost) {
      if (onNotEnoughCoins) onNotEnoughCoins();
      return;
    }

    setSending(true);
    setMessage("");

    try {
      await API.post("/gifts/send", {
        sender_email: currentUserEmail,
        receiver_email: receiverEmail,
        gift_id: gift.id,
        context,
        video_id: videoId,
      });

      // ===============================
      // 🎁 DÉCLENCHEMENT ANIMATION
      // ===============================
      let giftType = "small"; // par défaut

      if (gift.coin_cost > 5 && gift.coin_cost <= 50) {
        giftType = "medium";
      }

      if (gift.coin_cost > 50) {
        giftType = "large";
      }

      window.dispatchEvent(
        new CustomEvent("gift-animation", {
          detail: {
            name: gift.name,
            image: gift.image_url,
            value: gift.coin_cost,
            type: giftType, // small | medium | large
          },
        })
      );

      // 🔻 Mise à jour immédiate du solde (UX)
      setCoinsBalance((prev) => prev - gift.coin_cost);

      setMessage("🎁 Cadeau envoyé !");
      if (onSent) onSent();
    } catch (err) {
      console.error("❌ Erreur envoi cadeau :", err);
      setMessage(
        err.response?.data?.error || "Erreur lors de l'envoi du cadeau."
      );
    } finally {
      setSending(false);
    }
  };

  // ===============================
  // RENDER
  // ===============================
  return (
    <div className="gift-store-wrapper">
      {/* 💰 SOLDE */}
      <div className="gift-balance">
        💰 Mon solde : <span>{coinsBalance}</span> coins
      </div>

      {/* MESSAGE */}
      {message && <div className="gift-message">{message}</div>}

      {/* LOADING */}
      {loading && <p className="gift-loading">Chargement…</p>}

      {/* VIDE */}
      {!loading && gifts.length === 0 && (
        <p className="gift-empty">Aucun cadeau disponible.</p>
      )}

      {/* 🎁 CATALOGUE */}
      {!loading && gifts.length > 0 && (
        <div className="gift-catalogue">
          {gifts.map((gift) => (
            <div key={gift.id} className="gift-card">
              <img src={gift.image_url} alt={gift.name} />

              <div className="gift-name">{gift.name}</div>

              <div className="gift-price">
                {gift.coin_cost} 🪙
              </div>

              <button
                disabled={sending}
                onClick={() => handleSendGift(gift)}
              >
                Envoyer
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default GiftStore;
